import { Box, Button, Icon, Separator, Text } from "@salestrak/ui";

import type { FilterConfig, UseFiltersResult } from "#app/lib/filters/types";
import { FilterBlock } from "./filter-block";
import { LABEL_COL_WIDTH } from "./filter-row";
import { LogicSelect } from "./logic-select";

type AdvancedFiltersProps = {
  config: FilterConfig;
  filters: UseFiltersResult;
};

/**
 * Advanced tab content: block-based query builder.
 *
 * Renders filter blocks with AND/OR connectors between them.
 * Each block contains N filter rows that can be independently configured.
 * The main logic operator (between blocks) is shown as a Select between
 * block 0 and block 1.
 */
export function AdvancedFilters({ config, filters }: AdvancedFiltersProps) {
  const { advancedState } = filters;

  return (
    <Box paddingX="lg" paddingY="md">
      <Box gap="none">
        {advancedState.blocks.map((block, blockIndex) => {
          const isFirstConnector = blockIndex === 1;
          const connector = blockIndex > 0 ? (
            <Box
              key={`connector-${blockIndex}`}
              paddingTop="md"
              paddingBottom="md"
              paddingX="lg"
            >
              <Box direction="row" align="center" gap="sm">
                {isFirstConnector ? (
                  <Box width={LABEL_COL_WIDTH}>
                    <LogicSelect
                      value={advancedState.logic}
                      onChange={filters.setMainLogic}
                    />
                  </Box>
                ) : (
                  <Text size="sm" variant="muted" weight="medium">
                    {advancedState.logic}
                  </Text>
                )}
                <Box fill="flex">
                  <Separator />
                </Box>
              </Box>
            </Box>
          ) : null;

          return (
            <Box key={`block-stack-${blockIndex}`} gap="none">
              {connector}
              <FilterBlock
                config={config}
                block={block}
                onAddFilter={() => filters.addFilter(blockIndex)}
                onRemoveFilter={(filterIndex) =>
                  filters.removeFilter(blockIndex, filterIndex)
                }
                onUpdateFilter={(filterIndex, update) =>
                  filters.updateFilter(blockIndex, filterIndex, update)
                }
                onSetBlockLogic={(logic) =>
                  filters.setBlockLogic(blockIndex, logic)
                }
                onRemoveBlock={() => filters.removeBlock(blockIndex)}
              />
            </Box>
          );
        })}

        {/* Add filter / add block buttons */}
        <Box paddingTop="xs">
          <Box direction="row" gap="sm" align="start">
            <Button variant="ghost" size="sm" onClick={filters.addBlock}>
              <Icon name="plus" size="lg" />
              <Text>Add filter</Text>
            </Button>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
